import { CommonModule } from '@angular/common';
import { AfterRenderRef, AfterViewInit, Component, ElementRef, NgModule, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { CalendarModule } from '@app/primeng-overrides/calendar';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextareaModule } from 'primeng/inputtextarea';

import { GlobalModule } from '@app/_global/global.module';
import { HTMLEditorModule } from '@app/components/html-editor';
import { NomenclatureSelectorModule } from '@app/nomenclature/nomenclature-selector';
import { NomenclatureNodeData, NomenclatureService } from '@app/nomenclature/nomenclature.service';
import { Article, OperationModificative, Texte } from '@app/texte/texte.model';
import { OperationSelectorModule } from '../operation-selector';
import { TooltipModule } from 'primeng/tooltip';

import { ExtensibleObject, simpleComparison, startOfDay, uid } from '@helpers/utils';
import { EventManagerService, IEventListener } from '@global/event-manager.service';
import { primengTexts } from '@app/_helpers/primeng-texts';

@Component({
	selector: 'operation-dialog',
	templateUrl: './operation-dialog.html',
	providers: []
})
export class OperationDialogComponent implements OnInit, IEventListener {
	private _uuid: string = uid();
	get uuid(): string { return this._uuid; }

	@ViewChild('subTargetList', {static: false}) subTargetList: ElementRef<HTMLElement>;
	
	operation: OperationModificative;
	initialOperation: OperationModificative;

	isCreation: boolean;
	readonly: boolean;
	tomorrow: Date;
	changeSelectedTexte: boolean = false;

	nomenclatureFilter: NomenclatureNodeData | null = null;
	search_reference: string | null = null;
	search: string | null = null;
	suggestedTextes: Texte[] = [];
	suggestedTextesIntitules: string[] = [];
	textesLoading: boolean = false;
	targetTexte?: Texte;
	targetTexteLoading: boolean = false;
	targetArticle?: Article;

	primengTexts = primengTexts

	constructor(
		private config: DynamicDialogConfig,
		private dialogRef: DynamicDialogRef,
		private eventManager: EventManagerService,
		private nomenclatureService: NomenclatureService
	) {
		this.operation = config.data?.operation ?? {};
		this.isCreation = !config.data?.operation;
		this.readonly = config.data.readonly
	}

	simpleComparison = simpleComparison

	ngOnInit(): void {
		this.tomorrow = new Date();
		this.tomorrow.setDate(this.tomorrow.getDate() + 1);
		this.tomorrow = startOfDay(this.tomorrow);

		this.eventManager.registerEvent('close-modals', this, (args: any) => {
			this.dialogRef.close();
		});

		this.initialOperation = structuredClone(this.operation);

		if (!this.isCreation) {
			if (this.operation.txt_cible) {
				this.targetTexte = this.operation.txt_cible;
				this.targetArticle = this.targetTexte.articles.find((article: Article) => article.art_id == this.operation.art_id_cible);
				setTimeout(this.scrollIntoSubTarget, 0)
			}
			else {
				// Chargement du texte ciblé
				this.targetTexteLoading = true;
				this.nomenclatureService.getTexte(this.operation.txt_id_cible)
				.subscribe({
					next: (texte: Texte) => { 
						this.targetTexte = texte; 
						if (this.operation.art_id_cible) {
							this.targetArticle = this.targetTexte.articles.find((article: Article) => article.art_id == this.operation.art_id_cible);
							setTimeout(this.scrollIntoSubTarget, 0)
						}
					}
				})
				.add(() => { this.targetTexteLoading = false; });
			}
		}
	}

	scrollIntoSubTarget = () => {
		let targetedPositionElem = this.subTargetList.nativeElement.querySelector('.targeted-position');
		targetedPositionElem?.scrollIntoView({block: 'nearest'});

		let targetedArticleElem = this.subTargetList.nativeElement.querySelector('.targeted-article');
		targetedArticleElem?.scrollIntoView({block: 'nearest'});
	}

	typeChange() {
		this.targetArticle = undefined;
		this.operation.art_id_cible = null;
		this.operation.art_id_position = null;
		this.operation.art_corps = null;
	}

	fetchSuggestedTextes() {
		let params: ExtensibleObject = {order_by: 'txt_date'};

		if (this.nomenclatureFilter) {
			params.fam_id = this.nomenclatureFilter.fam_id;
			switch (this.nomenclatureFilter.typeNomenclature) {
			case 'theme':
				params.thm_id = this.nomenclatureFilter.thm_id;
				break;
			case 'rubrique':
				params.rub_id = this.nomenclatureFilter.rub_id;
				break;
			}
		}

		params.search_reference = this.search_reference;
		params.search = this.search;
		this.textesLoading = true;
		this.nomenclatureService.getTextes(params)
		.subscribe({
			next: ({ textes }) => {
				this.suggestedTextes = textes;
				this.suggestedTextesIntitules = textes.map((texte: Texte) => {
					let intitulé = texte.txt_intitule_plaintext ?? '';
					if (intitulé == '') {
						return '';
					}
					let maxCharNumber = 100;
					if(window.screen.width <= 1440) {
						maxCharNumber = 75;
					}
					if (intitulé.length > maxCharNumber) {
						intitulé = intitulé.slice(0, maxCharNumber) + '...';
					}
					return intitulé
				})
			}
		})
		.add(() => { this.textesLoading = false; });
	}


	setTargetTexte(txt_id: number) {
		if (this.operation.txt_id_cible != txt_id) {
			this.operation.txt_id_cible = txt_id;
			this.operation.art_corps = null;

			// Chargement du texte cible
			this.targetTexteLoading = true;
			this.nomenclatureService.getTexte(txt_id)
			.subscribe({
				next: (texte: Texte) => { this.targetTexte = texte; }
			})
			.add(() => { this.targetTexteLoading = false; });
		}
	}

	setTargetArticle(article: Article) {
		if (this.readonly) {
			return;
		}
		this.targetArticle = article;
		this.operation.art_id_cible = article.art_id;
		
		if (this.operation.tot_code == 'mod') {
			this.operation.art_corps = article.art_corps;
		}
	}

	setTargetPosition(pos: number | null) {
		if (this.readonly) {
			return;
		}
		this.operation.art_id_position = pos;
	}

	submit(){
		this.operation.txt_cible = this.targetTexte; // évite au code appelant le dialogue de charger le texte
		this.dialogRef.close(this.operation);
	}

	cancel() {
		this.dialogRef.close();
	}

	ngOnDestroy(): void {
		this.eventManager.unregisterEvent('close-modals', this);
	}
}


@NgModule({
	imports: [
		CommonModule,
		FormsModule,
		GlobalModule,
		ButtonModule,
		InputTextareaModule,
		NomenclatureSelectorModule,
		OperationSelectorModule,
		CalendarModule,
		DividerModule,
		HTMLEditorModule,
		TooltipModule
	],
	exports: [OperationDialogComponent],
	declarations: [OperationDialogComponent]
})
export class OperationDialogModule { }

